import { useState, useEffect, useContext } from 'react';
import { CategoryContext } from '../../providers/CategoryContext';
import { AuthContext } from '../../providers/AuthContext';
import { showError, showSuccess, showConfirm } from '../../utils/sweetAlert';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import Loader from '../shared/Loader';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const PriceManager = () => {
  const { categories } = useContext(CategoryContext);
  const { token } = useContext(AuthContext);
  const [priceStats, setPriceStats] = useState([]);
  const [priceConfig, setPriceConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [personalizedProducts, setPersonalizedProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productNewPrice, setProductNewPrice] = useState('');
  const [activeSection, setActiveSection] = useState('originals');

  const [aiDefaults, setAiDefaults] = useState({ cuadro: '', remera: '', almohadon: '', poster: '' });
  const [aiCategoryVariants, setAiCategoryVariants] = useState({ cuadro: '', remera: '', almohadon: '' });

  const [qtyBreakpoints, setQtyBreakpoints] = useState([1, 2, 6, 12]);
  const [qtyCategories, setQtyCategories] = useState([]);
  const [editingBreakpoints, setEditingBreakpoints] = useState(false);
  const [breakpointsInput, setBreakpointsInput] = useState('');
  const [editingQtyCategory, setEditingQtyCategory] = useState(null);
  const [qtyTierInputs, setQtyTierInputs] = useState({});

  const allowedOriginalCategories = ['Almohadones', 'Cuadros', 'Remeras', 'Canguros'];

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [productsRes, configRes, personalizedRes, qtyPricingRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/products/price-stats`),
        fetch(`${process.env.REACT_APP_API_URL}/price-config`),
        fetch(`${process.env.REACT_APP_API_URL}/products?category=Merchandising&limit=100`),
        fetch(`${process.env.REACT_APP_API_URL}/quantity-pricing`)
      ]);

      if (productsRes.ok) {
        const data = await productsRes.json();
        setPriceStats(data.filter(stat => allowedOriginalCategories.includes(stat.categoryName)));
      }

      if (configRes.ok) {
        const config = await configRes.json();
        setPriceConfig(config);
        if (config.aiProductDefaults) {
          setAiDefaults({ cuadro: config.aiProductDefaults.cuadro || '', remera: config.aiProductDefaults.remera || '', almohadon: config.aiProductDefaults.almohadon || '', poster: config.aiProductDefaults.poster || '' });
        }
        if (config.aiCategoryVariants) {
          setAiCategoryVariants({ cuadro: config.aiCategoryVariants.cuadro || '', remera: config.aiCategoryVariants.remera || '', almohadon: config.aiCategoryVariants.almohadon || '' });
        }
      }

      if (personalizedRes.ok) {
        const data = await personalizedRes.json();
        let products = [];
        if (Array.isArray(data)) products = data;
        else if (data.products) products = data.products;
        else if (data.pagination) {
          for (const key of Object.keys(data)) {
            if (Array.isArray(data[key]) && key !== 'pagination') { products = data[key]; break; }
          }
        }
        setPersonalizedProducts(products);
      }

      if (qtyPricingRes.ok) {
        const data = await qtyPricingRes.json();
        setQtyBreakpoints(data.breakpoints || [1, 2, 6, 12]);
        setQtyCategories(data.categories || []);
      }
    } catch (error) {
      showError('Error al cargar configuración de precios');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategoryPrice = async (categoryId, categoryName) => {
    if (!newPrice || isNaN(newPrice) || Number(newPrice) <= 0) { showError('Ingresa un precio válido'); return; }
    const result = await showConfirm(`¿Actualizar todos los productos de "${categoryName}" a $${newPrice}?`, 'Esta acción modificará el precio de todos los productos en esta categoría.');
    if (!result.isConfirmed) return;
    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/bulk-price`, {
        method: 'PUT', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ categoryId, newPrice: Number(newPrice) })
      });
      if (response.ok) { const data = await response.json(); showSuccess(`${data.modifiedCount} productos actualizados a $${newPrice}`); setEditingCategory(null); setNewPrice(''); fetchAllData(); }
      else { const error = await response.json(); showError(error.error || 'Error al actualizar precios'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const handleUpdateProductPrice = async (productId, productName) => {
    if (!productNewPrice || isNaN(productNewPrice) || Number(productNewPrice) <= 0) { showError('Ingresa un precio válido'); return; }
    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/${productId}`, {
        method: 'PUT', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ basePrice: Number(productNewPrice), price: Number(productNewPrice) })
      });
      if (response.ok) { showSuccess(`Precio de "${productName}" actualizado a $${productNewPrice}`); setEditingProduct(null); setProductNewPrice(''); fetchAllData(); }
      else { const error = await response.json(); showError(error.error || 'Error al actualizar precio'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const handleSaveAIDefaults = async () => {
    const result = await showConfirm('¿Guardar configuración de precios AI?', 'Esto guardará los precios por defecto para productos AI.');
    if (!result.isConfirmed) return;
    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/price-config/ai-defaults`, {
        method: 'PUT', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify(aiDefaults)
      });
      if (response.ok) { showSuccess('Configuración de precios AI guardada'); fetchAllData(); }
      else { const error = await response.json(); showError(error.error || 'Error al guardar'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const handleApplyToAIProducts = async () => {
    const result = await showConfirm('¿Aplicar precios a TODOS los productos AI?', 'Esto actualizará los basePrices de todos los productos en la colección productoais.');
    if (!result.isConfirmed) return;
    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/price-config/apply-to-ai-products`, {
        method: 'POST', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' })
      });
      if (response.ok) { const data = await response.json(); showSuccess(`${data.modifiedCount} productos AI actualizados`); }
      else { const error = await response.json(); showError(error.error || 'Error al aplicar precios'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const handleSaveAICategoryVariants = async () => {
    const result = await showConfirm('¿Guardar precios de variantes para categoría AI?', 'Esto guardará los precios de las variantes.');
    if (!result.isConfirmed) return;
    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/price-config/ai-category-variants`, {
        method: 'PUT', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify(aiCategoryVariants)
      });
      if (response.ok) { showSuccess('Precios de variantes guardados'); fetchAllData(); }
      else { const error = await response.json(); showError(error.error || 'Error al guardar'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const handleApplyToAICategory = async () => {
    const result = await showConfirm('¿Aplicar precios a productos de categoría AI?', 'Esto actualizará las variantes de todos los productos en la categoría AI.');
    if (!result.isConfirmed) return;
    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/price-config/apply-to-ai-category`, {
        method: 'POST', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' })
      });
      if (response.ok) { const data = await response.json(); showSuccess(`${data.modifiedCount} productos actualizados`); fetchAllData(); }
      else { const error = await response.json(); showError(error.error || 'Error al aplicar precios'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const handleSaveBreakpoints = async () => {
    const parsed = breakpointsInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n) && n >= 1);
    if (parsed.length === 0) { showError('Ingresa franjas válidas separadas por coma, ej: 1,2,6,12'); return; }
    const result = await showConfirm('¿Actualizar las franjas de cantidad globales?', 'Esto afecta a TODAS las categorías con precios por cantidad configurados.');
    if (!result.isConfirmed) return;
    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/quantity-pricing/breakpoints`, {
        method: 'PUT', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ breakpoints: parsed })
      });
      if (response.ok) { showSuccess('Franjas de cantidad actualizadas'); setEditingBreakpoints(false); fetchAllData(); }
      else { const error = await response.json(); showError(error.error || 'Error al actualizar franjas'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const handleSaveQtyCategoryTiers = async (categoryId, subcategory, pricingKey) => {
    const inputs = qtyTierInputs[pricingKey] || {};
    const tiers = qtyBreakpoints.map(minQuantity => ({
      minQuantity,
      price: inputs[minQuantity] !== undefined && inputs[minQuantity] !== '' ? Number(inputs[minQuantity]) : null
    }));
    if (tiers.some(t => t.price !== null && (isNaN(t.price) || t.price < 0))) {
      showError('Ingresa precios válidos'); return;
    }
    if (!tiers.some(t => t.price !== null)) {
      showError('Ingresa al menos un precio'); return;
    }
    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/quantity-pricing/${categoryId}`, {
        method: 'PUT', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ tiers, subcategory })
      });
      if (response.ok) { showSuccess('Precios por cantidad guardados'); setEditingQtyCategory(null); fetchAllData(); }
      else { const error = await response.json(); showError(error.error || 'Error al guardar'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const handleDeleteQtyCategoryTiers = async (categoryId, displayName, subcategory) => {
    const result = await showConfirm(`¿Desactivar precios por cantidad para "${displayName}"?`, 'Volverá a usar el precio único del producto.');
    if (!result.isConfirmed) return;
    setUpdating(true);
    try {
      const query = subcategory ? `?subcategory=${encodeURIComponent(subcategory)}` : '';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/quantity-pricing/${categoryId}${query}`, {
        method: 'DELETE', headers: getAdminAuthHeaders(token)
      });
      if (response.ok) { showSuccess('Precios por cantidad desactivados'); fetchAllData(); }
      else { const error = await response.json(); showError(error.error || 'Error al desactivar'); }
    } catch (error) { showError('Error de conexión'); } finally { setUpdating(false); }
  };

  const startEditingQtyCategory = (cat) => {
    const inputs = {};
    cat.tiers.forEach(t => { inputs[t.minQuantity] = t.price !== null ? t.price : ''; });
    setQtyTierInputs(prev => ({ ...prev, [cat.pricingKey]: inputs }));
    setEditingQtyCategory(cat.pricingKey);
  };

  if (loading) return <Loader />;

  const SECTIONS = [
    { id: 'originals', label: 'Originales', icon: 'fa-tags', count: priceStats.length },
    { id: 'ai-products', label: 'Productos AI', icon: 'fa-robot', count: 4 },
    { id: 'ai-category', label: 'Categoría AI', icon: 'fa-layer-group', count: 3 },
    { id: 'personalized', label: 'Merchandising', icon: 'fa-pen-ruler', count: personalizedProducts.length },
    { id: 'quantity', label: 'Precios por Cantidad', icon: 'fa-boxes-stacked', count: qtyCategories.filter(c => c.configured).length },
  ];

  const AI_PRODUCT_ITEMS = [
    { key: 'cuadro', label: 'Cuadro', icon: 'fa-image', placeholder: '1700' },
    { key: 'remera', label: 'Remera', icon: 'fa-tshirt', placeholder: '1600' },
    { key: 'almohadon', label: 'Almohadón', icon: 'fa-couch', placeholder: '1800' },
    { key: 'poster', label: 'Poster', icon: 'fa-scroll', placeholder: '1000' },
  ];

  const AI_CAT_ITEMS = [
    { key: 'cuadro', label: 'Cuadro', icon: 'fa-image', placeholder: '1700' },
    { key: 'remera', label: 'Remera', icon: 'fa-tshirt', placeholder: '1600' },
    { key: 'almohadon', label: 'Almohadón', icon: 'fa-couch', placeholder: '1800' },
  ];

  return (
    <div className="pricemgr-panel">
      {/* Header */}
      <div className="pricemgr-header">
        <div>
          <h1>Gestor de Precios</h1>
          <p>Modifica los precios de todos los productos por categoría</p>
        </div>
        <button className="pricemgr-reload-btn" onClick={fetchAllData}>
          <i className="fas fa-arrows-rotate"></i> Recargar
        </button>
      </div>

      {/* Section Tabs */}
      <div className="pricemgr-tabs">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            className={`pricemgr-tab ${activeSection === s.id ? 'active' : ''}`}
            onClick={() => setActiveSection(s.id)}
          >
            <i className={`fas ${s.icon}`}></i>
            {s.label}
            <span className="pricemgr-tab-count">{s.count}</span>
          </button>
        ))}
      </div>

      {/* === ORIGINALES === */}
      {activeSection === 'originals' && (
        <div className="pricemgr-section">
          <div className="pricemgr-section-desc">
            Modifica el precio de <strong>todos los productos</strong> en una categoría original de una vez.
          </div>
          <div className="pricemgr-table-card">
            <table className="pricemgr-table">
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th style={{ textAlign: 'center' }}>Productos</th>
                  <th style={{ textAlign: 'right' }}>Mín</th>
                  <th style={{ textAlign: 'right' }}>Máx</th>
                  <th style={{ textAlign: 'right' }}>Promedio</th>
                  <th style={{ textAlign: 'center' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {priceStats.map((stat) => (
                  <tr key={stat.categoryId}>
                    <td><strong>{stat.categoryName}</strong></td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="pricemgr-count-badge">{stat.count}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>${stat.minPrice?.toLocaleString('es-AR')}</td>
                    <td style={{ textAlign: 'right' }}>${stat.maxPrice?.toLocaleString('es-AR')}</td>
                    <td style={{ textAlign: 'right' }}><strong>${stat.avgPrice?.toLocaleString('es-AR')}</strong></td>
                    <td style={{ textAlign: 'center' }}>
                      {editingCategory === stat.categoryId ? (
                        <div className="pricemgr-inline-edit">
                          <input type="number" placeholder="Nuevo precio" value={newPrice}
                            onChange={(e) => setNewPrice(e.target.value)} disabled={updating} />
                          <button className="pricemgr-inline-save" onClick={() => handleUpdateCategoryPrice(stat.categoryId, stat.categoryName)} disabled={updating}>
                            {updating ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                          </button>
                          <button className="pricemgr-inline-cancel" onClick={() => { setEditingCategory(null); setNewPrice(''); }}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <button className="pricemgr-edit-btn" onClick={() => { setEditingCategory(stat.categoryId); setNewPrice(stat.minPrice || ''); }}>
                          <i className="fas fa-edit"></i> Cambiar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {priceStats.length === 0 && (
                  <tr><td colSpan="6" className="pricemgr-empty-row">No hay productos con categorías</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === PRODUCTOS AI === */}
      {activeSection === 'ai-products' && (
        <div className="pricemgr-section">
          <div className="pricemgr-section-desc">
            Precios por defecto para productos generados con IA. Se usan cuando un producto no tiene basePrices definidos.
          </div>
          <div className="pricemgr-price-grid">
            {AI_PRODUCT_ITEMS.map(item => (
              <div key={item.key} className="pricemgr-price-card">
                <div className="pricemgr-price-card-icon">
                  <i className={`fas ${item.icon}`}></i>
                </div>
                <label>{item.label}</label>
                <div className="pricemgr-price-input-wrap">
                  <span className="pricemgr-currency">$</span>
                  <input type="number" placeholder={item.placeholder}
                    value={aiDefaults[item.key]}
                    onChange={(e) => setAiDefaults({ ...aiDefaults, [item.key]: e.target.value })} />
                </div>
              </div>
            ))}
          </div>
          <div className="pricemgr-actions">
            <button className="pricemgr-save-btn" onClick={handleSaveAIDefaults} disabled={updating}>
              <i className="fas fa-save"></i> Guardar Configuración
            </button>
            <button className="pricemgr-apply-btn" onClick={handleApplyToAIProducts} disabled={updating}>
              <i className="fas fa-sync"></i> Aplicar a Todos los Productos AI
            </button>
          </div>
        </div>
      )}

      {/* === CATEGORÍA AI === */}
      {activeSection === 'ai-category' && (
        <div className="pricemgr-section">
          <div className="pricemgr-section-desc">
            Precios de las variantes para productos en la categoría "AI" de la tienda principal.
          </div>
          <div className="pricemgr-price-grid">
            {AI_CAT_ITEMS.map(item => (
              <div key={item.key} className="pricemgr-price-card">
                <div className="pricemgr-price-card-icon">
                  <i className={`fas ${item.icon}`}></i>
                </div>
                <label>{item.label}</label>
                <div className="pricemgr-price-input-wrap">
                  <span className="pricemgr-currency">$</span>
                  <input type="number" placeholder={item.placeholder}
                    value={aiCategoryVariants[item.key]}
                    onChange={(e) => setAiCategoryVariants({ ...aiCategoryVariants, [item.key]: e.target.value })} />
                </div>
              </div>
            ))}
          </div>
          <div className="pricemgr-actions">
            <button className="pricemgr-save-btn" onClick={handleSaveAICategoryVariants} disabled={updating}>
              <i className="fas fa-save"></i> Guardar Configuración
            </button>
            <button className="pricemgr-apply-btn" onClick={handleApplyToAICategory} disabled={updating}>
              <i className="fas fa-sync"></i> Aplicar a Productos de Categoría AI
            </button>
          </div>
        </div>
      )}

      {/* === MERCHANDISING === */}
      {activeSection === 'personalized' && (
        <div className="pricemgr-section">
          <div className="pricemgr-section-desc">
            Modifica el precio de cada producto de Merchandising individualmente.
          </div>
          <div className="pricemgr-table-card">
            <table className="pricemgr-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Imagen</th>
                  <th>Producto</th>
                  <th style={{ textAlign: 'right' }}>Precio Actual</th>
                  <th style={{ textAlign: 'center' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {personalizedProducts.map((product) => (
                  <tr key={product._id}>
                    <td>
                      <img src={getDisplayImageUrl(product.image || product.images?.[0])} alt={product.name}
                        className="pricemgr-thumb" onError={(e) => { e.target.src = '/placeholder.png'; }} />
                    </td>
                    <td>
                      <span className="pricemgr-product-name">{product.name}</span>
                      {product.subcategory && <small className="pricemgr-product-sub">{product.subcategory}</small>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>${(product.basePrice || product.price)?.toLocaleString('es-AR')}</strong>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {editingProduct === product._id ? (
                        <div className="pricemgr-inline-edit">
                          <input type="number" placeholder="Nuevo precio" value={productNewPrice}
                            onChange={(e) => setProductNewPrice(e.target.value)} disabled={updating} />
                          <button className="pricemgr-inline-save" onClick={() => handleUpdateProductPrice(product._id, product.name)} disabled={updating}>
                            {updating ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                          </button>
                          <button className="pricemgr-inline-cancel" onClick={() => { setEditingProduct(null); setProductNewPrice(''); }}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <button className="pricemgr-edit-btn" onClick={() => { setEditingProduct(product._id); setProductNewPrice(product.basePrice || product.price || ''); }}>
                          <i className="fas fa-edit"></i> Cambiar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {personalizedProducts.length === 0 && (
                  <tr><td colSpan="4" className="pricemgr-empty-row">No hay productos de Merchandising</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === PRECIOS POR CANTIDAD === */}
      {activeSection === 'quantity' && (
        <div className="pricemgr-section">
          <div className="pricemgr-section-desc">
            Configura descuentos por volumen: el precio unitario baja a medida que el cliente compra más unidades. Las franjas de cantidad son las mismas para todas las categorías; solo cambian los precios de cada franja.
          </div>

          <div className="pricemgr-table-card" style={{ marginBottom: '16px' }}>
            <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <strong>Franjas de cantidad:</strong>
              {editingBreakpoints ? (
                <>
                  <input type="text" placeholder="1,2,6,12" value={breakpointsInput}
                    onChange={(e) => setBreakpointsInput(e.target.value)} disabled={updating}
                    style={{ minWidth: '160px' }} />
                  <button className="pricemgr-inline-save" onClick={handleSaveBreakpoints} disabled={updating}>
                    {updating ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                  </button>
                  <button className="pricemgr-inline-cancel" onClick={() => setEditingBreakpoints(false)}>
                    <i className="fas fa-times"></i>
                  </button>
                </>
              ) : (
                <>
                  <span>{qtyBreakpoints.map(b => b === 1 ? '1 unidad' : `Desde ${b}`).join(' · ')}</span>
                  <button className="pricemgr-edit-btn" onClick={() => { setBreakpointsInput(qtyBreakpoints.join(',')); setEditingBreakpoints(true); }}>
                    <i className="fas fa-edit"></i> Cambiar franjas
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="pricemgr-table-card">
            <table className="pricemgr-table">
              <thead>
                <tr>
                  <th>Categoría</th>
                  {qtyBreakpoints.map(bp => (
                    <th key={bp} style={{ textAlign: 'right' }}>{bp === 1 ? '1 unidad' : `Desde ${bp}`}</th>
                  ))}
                  <th style={{ textAlign: 'center' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {qtyCategories.map((cat) => (
                  <tr key={cat.pricingKey}>
                    <td>
                      <strong>{cat.displayName || cat.categoryName}</strong>
                      {!cat.configured && <div><small className="pricemgr-product-sub">Sin configurar</small></div>}
                    </td>
                    {qtyBreakpoints.map(bp => (
                      <td key={bp} style={{ textAlign: 'right' }}>
                        {editingQtyCategory === cat.pricingKey ? (
                          <input type="number" placeholder="$" style={{ width: '90px', textAlign: 'right' }}
                            value={qtyTierInputs[cat.pricingKey]?.[bp] ?? ''}
                            onChange={(e) => setQtyTierInputs(prev => ({
                              ...prev,
                              [cat.pricingKey]: { ...prev[cat.pricingKey], [bp]: e.target.value }
                            }))}
                            disabled={updating} />
                        ) : (
                          (() => {
                            const tier = cat.tiers.find(t => t.minQuantity === bp);
                            return tier?.price !== null && tier?.price !== undefined
                              ? `$${Number(tier.price).toLocaleString('es-AR')}`
                              : <span className="pricemgr-product-sub">—</span>;
                          })()
                        )}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center' }}>
                      {editingQtyCategory === cat.pricingKey ? (
                        <div className="pricemgr-inline-edit">
                          <button className="pricemgr-inline-save" onClick={() => handleSaveQtyCategoryTiers(cat.categoryId, cat.subcategory, cat.pricingKey)} disabled={updating}>
                            {updating ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                          </button>
                          <button className="pricemgr-inline-cancel" onClick={() => setEditingQtyCategory(null)}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button className="pricemgr-edit-btn" onClick={() => startEditingQtyCategory(cat)}>
                            <i className="fas fa-edit"></i> Cambiar
                          </button>
                          {cat.configured && (
                            <button className="pricemgr-inline-cancel" onClick={() => handleDeleteQtyCategoryTiers(cat.categoryId, cat.displayName || cat.categoryName, cat.subcategory)} title="Desactivar">
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {qtyCategories.length === 0 && (
                  <tr><td colSpan={qtyBreakpoints.length + 2} className="pricemgr-empty-row">No hay categorías disponibles</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="pricemgr-note">
        <i className="fas fa-info-circle"></i>
        <div>
          <strong>Originales:</strong> Almohadones, Cuadros, Remeras y Canguros · 
          <strong> AI Generados:</strong> Variantes para productos con IA · 
          <strong> Categoría AI:</strong> Productos con categoría "AI" · 
          <strong> Merchandising:</strong> Precio individual · 
          <strong> Precios por Cantidad:</strong> Descuentos por volumen aplicados en producto y carrito
        </div>
      </div>
    </div>
  );
};

export default PriceManager;
